import React, { useMemo, useState, useContext } from 'react';
import { useUrlDateType } from '../Hooks/useUrlDateType';
import { useUrlSorting } from '../Hooks/useUrlSorting';
import { useUrlFilters, createFieldConfig, createDateConfig } from '../Hooks/useUrlFilters';
import { useViewFilters, usePaginationReset } from '../Hooks/useViewFilters';
import { useViewQuery } from '../Hooks/useViewQuery';
import { calculateRowCount } from '../utils/viewUtils';
import { useReusableTable } from '../common/TablePanel/tablePanel';
import { MaterialReactTable } from 'material-react-table';
import FiltersComponent from '../common/FiltersPanel/FIltersPanel';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { Box, ThemeProvider, Button, Tooltip } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SettingsIcon from '@mui/icons-material/Settings';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CodeIcon from '@mui/icons-material/Code';
import FilterDropdown from '../common/FilterDropdown/FilterDropdown';
import './PNPView.css';

const PREF_STORAGE_KEY = 'pnp_filter_preferences';
const DEFAULT_FIELDS = {
  vin: [],
  module: [],
  diagnostic_id: [],
  did: [],
};

const filterConfig = {
  dateFields: [createDateConfig('start_datetime', 'end_datetime', { startParam: 'startDate', endParam: 'endDate', allowEmpty: true })],
  debounceDelay: 100,
};

const PNPApp = ({ activeTheme }) => {
  const [dateType, handleDateTypeChange] = useUrlDateType();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 500 });
  const [sorting, setSorting] = useUrlSorting([{ id: 'datetime', desc: true }]);

  const mapping = { vin: 'vin', module: 'vescom', diagnostic_id: 'diagnostic_id', did: 'did' };

  const {
    singleValueFilters,
    debouncedSingleValueFilters,
    handleSingleFilterChange,
    handleSavePreferences,
    getOtherFilters,
    mergeField,
  } = useViewFilters({
    storageKey: PREF_STORAGE_KEY,
    defaultFields: DEFAULT_FIELDS,
    urlMapping: mapping,
    debounceDelay: 1000,
  });

  const { filters, debouncedFilters, handleDateRangeChange } = useUrlFilters(filterConfig);
  const { formatDateTime, createIsoStringWithTimezone, userTimezone, handleTimezoneChange } = useContext(TimezoneContext);

  const effectiveTimezone = userTimezone;

  const mergedFilters = useMemo(() => {
    return {
      ...debouncedFilters,
      vin: mergeField(debouncedSingleValueFilters.vin, debouncedFilters.vin),
      module: mergeField(debouncedSingleValueFilters.module, debouncedFilters.module),
      diagnostic_id: mergeField(debouncedSingleValueFilters.diagnostic_id, debouncedFilters.diagnostic_id),
      did: mergeField(debouncedSingleValueFilters.did, debouncedFilters.did),
    };
  }, [debouncedFilters, debouncedSingleValueFilters, mergeField]);

  usePaginationReset(mergedFilters, dateType, setPagination);

  const pnpQuery = useViewQuery({
    queryKey: 'pnp-data',
    mergedFilters,
    pagination,
    sorting,
    dateType,
    urlMapping: mapping,
    createIsoStringWithTimezone,
    endpoint: 'api/v2/part_number_pulls/query',
  });

  const data = useMemo(() => pnpQuery.data?.results || [], [pnpQuery.data]);
  const rowCount = useMemo(() => calculateRowCount(pnpQuery.data, data, pagination), [pnpQuery.data, data, pagination]);

  const columns = useMemo(() => [
    { accessorKey: 'vin', header: 'VIN' },
    { accessorKey: 'date', header: 'Date', Cell: ({ cell }) => formatDateTime(cell.getValue(), 'YYYY-MM-DD HH:mm:ss') },
    { accessorKey: 'module', header: 'Module' },
    { accessorKey: 'diagnostic_id', header: 'Diagnostic ID' },
    { accessorKey: 'did', header: 'DID' },
    {
      accessorKey: 'response',
      header: 'Response',
      size: 200,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (value == null) return '';
        const str = String(value);
        if (str.length <= 30) return str;
        return (
          <Tooltip title="Expand row to see full response" arrow>
            <span style={{ cursor: 'default' }}>
              {str.slice(0, 30)}…
            </span>
          </Tooltip>
        );
      },
    },
  ], [formatDateTime]);

  const dropdowns = useMemo(() => (
    <>
      <Box sx={{ mt: 2 }}>
        <FilterDropdown
          field="vin"
          label="VIN"
          value={singleValueFilters.vin}
          onChange={(value) => handleSingleFilterChange('vin', value)}
          apiBaseUrl="/api/part_number_pulls"
          multiple={true}
          icon={DirectionsCarIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('vin')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="module"
          label="Module"
          value={singleValueFilters.module}
          onChange={(value) => handleSingleFilterChange('module', value)}
          apiBaseUrl="/api/part_number_pulls"
          multiple={true}
          icon={SettingsIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('module')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="diagnostic_id"
          label="Diagnostic ID"
          value={singleValueFilters.diagnostic_id}
          onChange={(value) => handleSingleFilterChange('diagnostic_id', value)}
          apiBaseUrl="/api/part_number_pulls"
          multiple={true}
          icon={MedicalServicesIcon}
          disableSearch={true}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('diagnostic_id')}
          fieldMapping={mapping}
        />
        <FilterDropdown
          field="did"
          label="DID"
          value={singleValueFilters.did}
          onChange={(value) => handleSingleFilterChange('did', value)}
          apiBaseUrl="/api/part_number_pulls"
          multiple={true}
          icon={CodeIcon}
          dateFilters={{
            start_datetime: debouncedFilters.start_datetime,
            end_datetime: debouncedFilters.end_datetime,
            dateType,
            createIsoStringWithTimezone,
          }}
          otherFilters={getOtherFilters('did')}
          fieldMapping={mapping}
        />
      </Box>
    </>
  ), [
    singleValueFilters,
    handleSingleFilterChange,
    debouncedFilters,
    dateType,
    createIsoStringWithTimezone,
    getOtherFilters,
    mapping
  ]);

  const { table } = useReusableTable({
    columns,
    data,
    rowCount: rowCount,
    isLoading: pnpQuery.isFetching,
    enableRowSelection: false,
    enableRowExpansion: false,
    enableRowActions: false,
    enableCompareFiles: false,
    enableRowVirtualization: true,
    initialPageSize: 500,
    maxHeight: 'calc(100vh - 250px)',
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 250px)' } },
    enableDefaultPreview: false,
    enableDefaultDownload: false,
    enableDefaultAddToCart: false,
    enableCSVExport: true,
    csvExportFilename: 'part-number-pull-export.csv',
    getRowId: (row) => row.id,
    pagination,
    sorting,
    onSortingChange: setSorting,
    manualPagination: true,
    onPaginationChange: setPagination,
    paginationDisplayMode: 'default',
    muiPaginationProps: { 
      color: 'primary', 
      shape: 'rounded', 
      variant: 'outlined', 
      rowsPerPageOptions: [500, 1000],
      showFirstButton: false,
      showLastButton: false
    },
    renderDetailPanel: ({ row }) => {
      const response = row.original?.response;
      if (response === null || response === undefined || response === '') {
        return (
          <Box sx={{ p: 2, color: 'text.secondary' }}>
            No response available for this row.
          </Box>
        );
      }
      return (
        <Box className="pnp-response-detail">
          {String(response)}
        </Box>
      );
    },
  });


  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="pnp-main-container">
        <Box>
          <FiltersComponent
            activeTheme={activeTheme}
            dateRangePicker={{ filters: filters || {}, handleDateRangeChange }}
            timezoneSelector={{ timezone: effectiveTimezone, userTimezone, handleTimezoneChange }}
            dateTypeSelector={{ dateType, handleDateTypeChange }}
            onSaveSearch={handleSavePreferences}
            storageKey={PREF_STORAGE_KEY}
            dropdowns={dropdowns}
          />
        </Box>
        <Box className="pnp-content">
          <MaterialReactTable table={table} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default PNPApp;
